import React, { useCallback, useMemo, useState } from "react";
import {
  ModalDialogConfirmStatus,
  ModalDialogConfirmStatusProps,
} from "components";
import { ProcessState } from "types/process";
import { ActionButton, ActionButtonProps } from "components";

type DialogPassthroughProps = Omit<
  ModalDialogConfirmStatusProps,
  "open" | "title" | "onClose" | "onCancelButtonClick"
>;

export interface ActionButtonConfirmStatusProps extends DialogPassthroughProps {
  // Button props
  ariaLabel?: ActionButtonProps["ariaLabel"];
  children?: ActionButtonProps["children"]; //Button text / content (ignored when iconOnly is true)
  color?: ActionButtonProps["color"];
  disabled?: ActionButtonProps["disabled"];
  buttonFullWidth?: ActionButtonProps["fullWidth"];
  size?: ActionButtonProps["size"];
  variant?: ActionButtonProps["variant"];

  // Icon props
  icon?: ActionButtonProps["icon"];
  iconOnly?: ActionButtonProps["iconOnly"];

  //Tooltip Props
  tooltipText?: ActionButtonProps["tooltipText"];
  tooltipArrow?: ActionButtonProps["tooltipArrow"];
  tooltipPlacement?: ActionButtonProps["tooltipPlacement"];

  // Dialog props
  dialogTitle?: string;

  /**
   * Called immediately before the dialog opens.
   * Intended for resetting parent-owned state (processState, message, result).
   */
  onDialogOpen?: () => void;
}

export const ActionButtonConfirmStatus: React.FC<
  ActionButtonConfirmStatusProps
> = (props: ActionButtonConfirmStatusProps) => {
  const {
    // Button Props
    ariaLabel,
    children,
    color = "primary",
    disabled = false,
    buttonFullWidth = false,
    size = "medium",
    variant = "contained",

    // Icon props
    icon,
    iconOnly = false,

    // Tooltip props
    tooltipText = "",
    tooltipArrow = true,
    tooltipPlacement = "top",

    // Dialog props
    dialogTitle = "Confirm Action",

    // Caller-controlled props that affect wrapper behavior
    processState = ProcessState.IDLE,

    // Lifecycle hook
    onDialogOpen,

    // Everything else passes through to the dialog
    ...otherProps
  } = props;
  const [open, setOpen] = useState(false);

  const isBusy = useMemo(() => {
    return (
      processState === ProcessState.INITIALIZING ||
      processState === ProcessState.QUEUED ||
      processState === ProcessState.PROCESSING
    );
  }, [processState]);

  const handleOpen = useCallback(() => {
    onDialogOpen?.();
    setOpen(true);
  }, [onDialogOpen]);

  const handleClose = useCallback(() => {
    if (isBusy) return;
    setOpen(false);
  }, [isBusy]);

  return (
    <>
      <ActionButton
        ariaLabel={ariaLabel}
        color={color}
        disabled={disabled}
        fullWidth={buttonFullWidth}
        size={size}
        variant={variant}
        onClick={handleOpen}
        icon={icon}
        iconOnly={iconOnly}
        tooltipText={tooltipText}
        tooltipArrow={tooltipArrow}
        tooltipPlacement={tooltipPlacement}
      >
        {children}
      </ActionButton>

      <ModalDialogConfirmStatus
        {...otherProps}
        //Dialog Props
        open={open}
        title={dialogTitle}
        onClose={handleClose}
        //Action Button Props
        onCancelButtonClick={handleClose}
        //Status Props
        processState={processState}
      />
    </>
  );
};
